import { formatNumber } from "./format.ts";
import { ChartFrame, PatternDefs, PatternedRect, useChartId } from "./frame.tsx";
import { Legend } from "./Legend.tsx";
import { type ChartSeries, resolveSeries, type SeriesDatum } from "./series.ts";
import { useChartStrings } from "./strings.ts";

export interface StackedBarChartProps {
  title: string;
  summary: string;
  series: readonly ChartSeries[];
  /** One stacked bar per datum (typically a day), segments in series order from the bottom. */
  data: readonly SeriesDatum[];
  formatValue?: (value: number) => string;
  formatLabel?: (label: string) => string;
  labelHeader: string;
  className?: string;
}

const identity = (label: string) => label;

/** Stacked bars over time; each series has its own colour and pattern, named in the legend. */
export function StackedBarChart({
  title,
  summary,
  series,
  data,
  formatValue = formatNumber,
  formatLabel = identity,
  labelHeader,
  className,
}: StackedBarChartProps) {
  const id = useChartId();
  const strings = useChartStrings();
  const resolved = resolveSeries(series);
  const totals = data.map((d) => resolved.reduce((sum, s) => sum + (d.values[s.key] ?? 0), 0));
  const max = Math.max(0, ...totals);
  const step = 10;
  const width = Math.max(data.length, 1) * step;
  const seriesTotals = Object.fromEntries(
    resolved.map((s) => [
      s.key,
      formatValue(data.reduce((sum, d) => sum + (d.values[s.key] ?? 0), 0)),
    ]),
  );
  const table = {
    columns: [labelHeader, ...resolved.map((s) => s.label), strings.total],
    rows: data.map((d, i) => [
      formatLabel(d.label),
      ...resolved.map((s) => formatValue(d.values[s.key] ?? 0)),
      formatValue(totals[i] ?? 0),
    ]),
  };
  const first = data[0];
  const last = data.at(-1);
  return (
    <ChartFrame title={title} summary={summary} table={table} className={className}>
      {({ summaryId }) => (
        <div className="flex flex-col gap-2">
          <Legend series={resolved} values={seriesTotals} />
          <div className="flex items-stretch gap-2">
            <div className="flex flex-col justify-between text-right text-xs tabular-nums text-chart-label">
              <span>{formatValue(max)}</span>
              <span>{formatValue(0)}</span>
            </div>
            <svg
              role="img"
              aria-describedby={summaryId}
              viewBox={`0 0 ${width} 100`}
              preserveAspectRatio="none"
              className="h-32 w-full min-w-0 border-b border-chart-grid"
            >
              <title>{title}</title>
              <PatternDefs id={id} />
              {data.map((d, i) => {
                let y = 100;
                return (
                  <g key={d.label}>
                    <title>{`${formatLabel(d.label)}: ${formatValue(totals[i] ?? 0)}`}</title>
                    {resolved.map((s) => {
                      const value = d.values[s.key] ?? 0;
                      const height = max > 0 ? (value / max) * 100 : 0;
                      y -= height;
                      return height > 0 ? (
                        <PatternedRect
                          key={s.key}
                          chartId={id}
                          tone={s.tone}
                          pattern={s.pattern}
                          x={i * step + 1.5}
                          y={y}
                          width={step - 3}
                          height={height}
                        />
                      ) : null;
                    })}
                  </g>
                );
              })}
            </svg>
          </div>
          {first && last ? (
            <div className="flex justify-between pl-8 text-xs text-chart-label" aria-hidden="true">
              <span>{formatLabel(first.label)}</span>
              {last !== first ? <span>{formatLabel(last.label)}</span> : null}
            </div>
          ) : null}
        </div>
      )}
    </ChartFrame>
  );
}
